export const DEFAULT_LOCALE = 'ko' as const;
export const PREFIX_LOCALES = ['en', 'ja'] as const;
export const SUPPORTED_LOCALES = [DEFAULT_LOCALE, ...PREFIX_LOCALES] as const;

export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export type PrefixedLocale = (typeof PREFIX_LOCALES)[number];

export const LOCALE_PREF_KEY = 'st_locale_pref';

export function isAppLocale(value: string | null | undefined): value is AppLocale {
  return value === 'ko' || value === 'en' || value === 'ja';
}

export function isPrefixedLocale(value: string): value is PrefixedLocale {
  return value === 'en' || value === 'ja';
}

/** pathname에서 locale을 추출. `/en/...` → en, `/ja` → ja, 그 외 → ko */
export function getLocaleFromPathname(pathname: string): AppLocale {
  const segment = pathname.split('/').filter(Boolean)[0];
  if (segment && isPrefixedLocale(segment)) return segment;
  return DEFAULT_LOCALE;
}

/** `/en/blog/faq` → `/blog/faq`, `/ja` → `/` */
export function stripLocalePrefix(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return '/';
  if (isPrefixedLocale(segments[0])) {
    const rest = segments.slice(1).join('/');
    return rest ? `/${rest}` : '/';
  }
  return pathname.startsWith('/') ? pathname : `/${pathname}`;
}

/** locale에 맞는 path 생성. ko는 prefix 없음 */
export function withLocale(path: string, locale: AppLocale): string {
  const normalized =
    !path || path === '/'
      ? '/'
      : path.startsWith('/')
        ? path
        : `/${path}`;
  const bare = stripLocalePrefix(normalized);

  if (locale === DEFAULT_LOCALE) return bare;
  if (bare === '/') return `/${locale}`;
  return `/${locale}${bare}`;
}

export function getStoredLocalePref(): AppLocale | null {
  try {
    const raw = localStorage.getItem(LOCALE_PREF_KEY);
    return isAppLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function setStoredLocalePref(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_PREF_KEY, locale);
  } catch {
    // ignore quota / private mode
  }
}
